import { Autocomplete, TextField, useTheme } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { type FileRecord } from "@/shared/domain/files";

import { useWorkspaceFiles } from "../../WorkspaceCtx";

import styles from "./FileSelector.module.css";

interface FileSelectorProps {
  className?: classNames.Argument;
  onSelect: (selection: string) => void;
  selection?: string; // file_id

  /** Render the input in an error state. */
  isError?: boolean;

  /** Helper text rendered below the input. Styled as an error when `isError` is true. */
  helperText?: React.ReactNode;

  /**
   * Render the selector even when fewer than two files are available.
   * Default behavior in single-file workspaces is to return null, since
   * there is no selection to make. Callers that need to surface an error
   * state (e.g. a dangling file reference) pass `forceShow`.
   */
  forceShow?: boolean;

  /**
   * Files to show in the picker. Defaults to all workspace files. Callers
   * that want to scope the picker (e.g. only data files) pass a filtered
   * list — see `useDataFiles`.
   */
  files?: FileRecord[];
}

export function FileSelector({
  className,
  onSelect,
  selection,
  isError,
  helperText,
  forceShow,
  files: filesProp,
}: FileSelectorProps) {
  const theme = useTheme();

  const workspaceFiles = useWorkspaceFiles();
  const files = filesProp ?? workspaceFiles;
  const fileIdToFileMap = React.useMemo(() => {
    const map = new Map<string, FileRecord>();
    for (const file of files) {
      map.set(file.file_id, file);
    }
    return map;
  }, [files]);

  const onChange = React.useCallback(
    (_: React.SyntheticEvent, selection: FileRecord | null) => {
      if (selection === null) {
        return;
      }
      onSelect(selection.file_id);
    },
    [onSelect],
  );

  if (files.length < 2 && !forceShow) {
    return null;
  }

  const value =
    selection !== undefined ? fileIdToFileMap.get(selection) : undefined;

  return (
    <Autocomplete
      className={classNames(className)}
      disableClearable
      fullWidth
      getOptionKey={(file) => file.file_id}
      getOptionLabel={(file) => file.relative_path ?? file.file_id}
      onChange={onChange}
      options={files}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label="File"
          error={isError}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            className: styles.input,
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            className: styles.input,
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} className={classNames(props.className, styles.option)}>
            <div className={styles.primary}>{option.relative_path}</div>
            <div
              className={styles.secondary}
              style={{ color: theme.palette.text.secondary }}
            >
              <span className={styles.label}>Dataset: </span>
              <span>{option.association_id}</span>
            </div>
          </li>
        );
      }}
      size="small"
      value={value}
    />
  );
}
